/* eslint-disable react-hooks/rules-of-hooks */
/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react/forbid-prop-types */
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { useMutation } from 'react-query';
import BigNumber from 'bignumber.js';
import { isMobile } from 'react-device-detect';
import ModalBodyLayout from '../../../shared/ModalBodyLayout';
import { useRateDetails } from '../../../../utilities/sharedHooks/rates';
import { currencies } from '../../../../utilities/constants';
import { formatNumber, calculateMonthlyInterest } from '../../../../utilities/formatHelpers';
import { LOAN_PAY_INTEREST } from '../../mutations';
import { showAlert } from '../../../../utilities/notifications';
import BalanceByWallet from '../../../../components/BalanceByWallet';
import { MODAL_SCREENS } from '../../constants';

const PayInterest = ({
  loanDataObj,
  showScreen,
  hideModal,
  setExtraCollateralOptions,
  onExchangeClick,
  onReceiveClick,
  setCurrentWallet,
  currentWallet,
  useableWallets,
}) => {
  const { loanMetaData, loanData } = loanDataObj;
  useState(() => {
    if (!currentWallet) {
      const walletToUse = useableWallets.find(
        (w) => w.coin.toLowerCase() === loanMetaData.collateral_currency.toLowerCase()
      );
      setCurrentWallet(walletToUse);
    }
  }, [useableWallets, loanMetaData, currentWallet]);
  const { t: translate } = useTranslation();
  const { rate } = useRateDetails(currentWallet?.coin, loanMetaData.principal_currency);
  const principalAmountRaw =
    loanData.principal_amount * currencies[loanMetaData.principal_currency].conversion;
  const monthlyInterestDue = calculateMonthlyInterest(
    loanMetaData.interest_apr,
    principalAmountRaw
  );
  const missedPayments = loanData?.missed_payments.length;
  const walletBalance = useMemo(
    () =>
      formatNumber(
        currentWallet?.available_balance * currencies[loanMetaData.principal_currency].conversion,
        currencies[loanMetaData.principal_currency].decimal
      ),
    [currentWallet]
  );

  const { total_amount: overdueInterestAmountDb } =
    loanData.missed_payments.length === 1
      ? { total_amount: loanData.missed_payments[0].total_amount }
      : loanData.missed_payments.reduce((acc, payment) => ({
          total_amount: parseFloat(acc.total_amount) + parseFloat(payment.total_amount),
        }));

  const overdueInterestAmount = formatNumber(
    overdueInterestAmountDb * currencies[loanData.principal_currency].conversion,
    currencies[loanData.principal_currency].decimal
  );

  const selectedWalletFiatBalance = formatNumber(
    walletBalance * rate,
    currencies[currentWallet?.coin].decimal
  );

  const { isLoading: isRepayingLoanInterest, mutate: payLoanInterest } = useMutation(
    LOAN_PAY_INTEREST,
    {
      onSettled(_, error) {
        if (error) {
          showAlert({ message: error.message, type: 'danger' });
        } else {
          hideModal();
          showAlert({
            message: translate('loan_interest_payment_success'),
            type: 'success',
          });
        }
      },
    }
  );

  const walletBalanceIsGood = useMemo(
    () => new BigNumber(overdueInterestAmount).lte(selectedWalletFiatBalance),
    [selectedWalletFiatBalance]
  );

  const handleReceiveCrypto = () => {
    setExtraCollateralOptions({
      collateralWallet: currentWallet,
      minCollateralAmountNeeded: overdueInterestAmount,
    });
    onReceiveClick();
  };

  const renderFooter = () => (
    <button
      className="btn btn-primary"
      disabled={isRepayingLoanInterest || !walletBalanceIsGood}
      type="button"
      onClick={() => {
        payLoanInterest({
          sourceWalletCurrency: currentWallet?.coin,
          loanId: loanData.id,
        });
      }}>
      {translate('continue_btn_title')}
    </button>
  );

  return (
    <ModalBodyLayout
      withBack
      title={translate('add_collateral_pay_interest')}
      renderFooter={renderFooter}
      onPrevClick={() => {
        showScreen(MODAL_SCREENS.LOAN_DETAILS, loanMetaData);
      }}
      scrollStyle={isMobile ? { maxHeight: '31.5rem' } : false}
      className="pb-4">
      <div>
        <div>
          <h2 className="text-primary m-0 text-uppercase">
            {overdueInterestAmount} {loanMetaData.principal_currency}
          </h2>
          <p className="text-shadow text-f6">{translate('loan_pay_interest_note')}</p>
        </div>
        <div className="mt-3 pt-0 text-f6 text-shadow text-start">
          <div className="rounded-extra bg-light mt-3">
            <div className="flex-row-center px-3 py-3 border-bottom">
              <p className="text-f6 text-shadow">
                {translate('loan_pay_interest_monthly_interest_due')}
              </p>
              <b className="text-f6 text-dark text-uppercase">
                {monthlyInterestDue} {loanMetaData.principal_currency}
              </b>
            </div>
            <div className="flex-row-center px-3 py-3">
              <p className="text-f6 text-shadow">{translate('loan_pay_interest_total_month')}</p>
              <b className="text-f6 text-dark text-uppercase">{missedPayments}</b>
            </div>
          </div>
        </div>
        {!walletBalanceIsGood && (
          <div
            className="alert alert-danger alert-action border-0 cursor-pointer mt-4 py-3 mb-0"
            role="alert">
            <i className="text-danger icon icon-cancel-icon pe-none" />
            <span className="content text-start pe-none">
              <p className="m-0">
                {translate('loan_interest_pay_note').replace(
                  '_COIN_',
                  currencies[currentWallet?.coin.toUpperCase()].fullName
                )}
              </p>
            </span>
          </div>
        )}
        {currentWallet && (
          <BalanceByWallet
            principalCurrency={loanMetaData.principal_currency}
            onChange={() => {}}
            customOnClick
            onClick={() => {
              showScreen(MODAL_SCREENS.WALLETS, { loanMetaData, loanData });
            }}
            walletBalanceIsGood={walletBalanceIsGood}
            selectedWallet={currentWallet}
          />
        )}
        {!walletBalanceIsGood && (
          <div>
            <div className="bg-light shadow-sm container rounded-3 p-3 cursor-pointer mt-3">
              <div
                tabIndex={0}
                role="button"
                className="flex-row d-flex align-items-center justify-content-start w-100"
                onClick={handleReceiveCrypto}
                onKeyDown={() => {}}>
                <div className="col-1">
                  <i
                    className="icon-circle-card"
                    style={{
                      width: '1.5rem',
                      height: '1.5rem',
                      fontSize: '1.7rem',
                      textIndent: '-0.125rem',
                    }}
                  />
                </div>
                <div className="col-10 ps-3 d-flex flex-column justify-content-center align-items-start">
                  <p className="h2 text-f5">
                    {translate('loan_transfer_coin').replace(
                      '_COIN_',
                      currentWallet?.coin.toUpperCase()
                    )}
                  </p>
                  <span className="text-f6 text-shadow">
                    {translate('loan_transfer_coin_text').replace(
                      '_COIN_',
                      currentWallet?.coin.toUpperCase()
                    )}
                  </span>
                </div>
                <div className="col-1 d-flex justify-content-end ms-auto">
                  <i
                    style={{ width: '1rem' }}
                    className="icon icon-arrow-next-navigation text-primary text-f4 cursor-pointer"
                    aria-label={translate('loan_transfer_coin_text')}
                  />
                </div>
              </div>
            </div>
            <div
              className="bg-light shadow-sm container rounded-3 p-3 cursor-pointer mt-3 mb-5"
              role="button"
              tabIndex={0}
              onKeyDown={() => {}}
              onClick={() => {
                hideModal();
                onExchangeClick();
              }}>
              <div className="flex-row d-flex align-items-center justify-content-start w-100">
                <div className="col-1">
                  <i
                    className=" icon-buy-sell-menu"
                    style={{
                      width: '1.5rem',
                      height: '1.5rem',
                      fontSize: '1.7rem',
                      textIndent: '-0.125rem',
                    }}
                  />
                </div>
                <div className="col-10 ps-3 d-flex flex-column justify-content-center align-items-start">
                  <p className="h2 text-f5">{translate('loan_exchange_coin')}</p>
                  <span className="text-f6 text-shadow">
                    {translate('loan_exchange_coin_text').replace(
                      '_COIN_',
                      currentWallet?.coin.toUpperCase()
                    )}
                  </span>
                </div>
                <div className="col-1 d-flex justify-content-end ms-auto">
                  <i
                    style={{ width: '1rem' }}
                    className="icon icon-arrow-next-navigation text-primary text-f4 cursor-pointer"
                    aria-label={translate('loan_exchange_coin_text')}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </ModalBodyLayout>
  );
};

export default PayInterest;

PayInterest.propTypes = {
  loanDataObj: PropTypes.object,
  showScreen: PropTypes.func.isRequired,
  hideModal: PropTypes.func.isRequired,
  setExtraCollateralOptions: PropTypes.func.isRequired,
  onExchangeClick: PropTypes.func.isRequired,
  onReceiveClick: PropTypes.func.isRequired,
  currentWallet: PropTypes.object,
  useableWallets: PropTypes.array.isRequired,
  setCurrentWallet: PropTypes.func.isRequired,
};
PayInterest.defaultProps = {
  loanDataObj: null,
  currentWallet: null,
};
